import {
  FileUploadPattern,
  FormDate,
  FormGroup,
  FormRadioGroup,
  InPageAlert,
  Input,
} from '@snsw/react-component-library';
import React, { useEffect, useState } from 'react';
import { PLANT_API_BASE_URL, radioOptionsYesNo } from 'shared/constants/constants';
import { isEmpty, validateExt } from 'shared/utils/Validation';
import styles from './PlantItemDetails.module.scss';
import moment from 'moment';
import axios from 'axios';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';

export const InspectionDocumentsUpload = React.forwardRef((props, ref) => {
  const {
    id = '',
    value = {},
    onChange = () => {},
    validateInspectiondate = () => {},
    applicationId,
    onErrorChange = () => {},
    warnInspectiondate = () => {},
    supportingDocumentCode,
    description,
    labels = PlantItemDetailsPage.relatedDetails.inspectionDocumentUpload,
    maxFiles = 10,
  } = props;

  const {
    competentPerson: savedCompetentPerson = '',
    inspectionDate: savedInspectionDate = '',
    anySpecialConditions: savedAnySpecialConditions = '',
    specialConditions: savedSpecialConditions = '',
    uploadedFiles: savedUploadedFiles = [],
  } = value;

  const [competentPerson, setCompetentPerson] = useState(savedCompetentPerson);
  const [inspectionDate, setInspectionDate] = useState(savedInspectionDate);
  const [anySpecialConditions, setAnySpecialConditions] = useState(savedAnySpecialConditions);
  const [specialConditions, setSpecialConditions] = useState(savedSpecialConditions);
  const [uploadedFiles, setUploadedFiles] = useState(savedUploadedFiles);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);
  const [inspectionDateWarning, setInspectionDateWarning] = useState();

  const validators = [
    {
      fieldName: `${id}_competentPerson`,
      fieldValue: competentPerson,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: `${id}_inspectionDate`,
      fieldValue: inspectionDate,
      isValid: (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && moment(v).isBefore(moment()) && !validateInspectiondate(v),
      errorMessage: (v) => validateInspectiondate(v) || 'Please enter valid date',
    },
    {
      fieldName: `${id}_anySpecialConditions`,
      fieldValue: anySpecialConditions,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: `${id}_conditions`,
      fieldValue: specialConditions,
      isValid: (v) => !isEmpty(v) || isEmpty(anySpecialConditions) || anySpecialConditions === 'no',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: `${id}_upload`,
      fieldValue: uploadedFiles,
      isValid: (v) => v.length > 0,
      errorMessage: () => 'Please attach file',
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  let attachmentEndpointUrl = `${PLANT_API_BASE_URL}/application/${applicationId}/attachments`;

  const attachmentApi = {
    uploadFile: async (file) => {
      const content = new FormData();
      content.append('file', file);
      const options = {
        url: `${attachmentEndpointUrl}/${file.name}`,
        method: 'post',
        data: content,
      };
      const response = await axios(options);
      return response.data;
    },
    removeFile: async (fileId) => {
      const response = await axios.delete(`${attachmentEndpointUrl}/${fileId}`);
      return response.data;
    },
  };

  useEffect(() => {
    let result = {
      competentPerson,
      inspectionDate,
      anySpecialConditions,
      ...(anySpecialConditions === 'yes' && { specialConditions }),
      ...(anySpecialConditions === 'no' && { specialConditions: '' }),
      uploadedFiles,
      attachments: formatAttachments(uploadedFiles),
    };

    onChange(result);

    setInspectionDateWarning(warnInspectiondate(inspectionDate));

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [competentPerson, inspectionDate, anySpecialConditions, specialConditions, uploadedFiles]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  const formatAttachments = (files) => {
    return files.map((f) => ({
      amandaCode: parseInt(supportingDocumentCode.code),
      name: f.name,
      attachmentID: f.id,
      description: description,
      status: 'Portal-Submitted',
      category: 'Supporting Documents',
      type: supportingDocumentCode.description,
    }));
  };

  return (
    <>
      <FormGroup
        id={id + '_competentPerson'}
        label={labels.competentPersonLabel}
        isRequired
        errorMessage={errors[`${id}_competentPerson`]?.errorMessage}
        hasError={showErrors && errors[`${id}_competentPerson`]?.invalid}
        margin={{ top: 'lg' }}
      >
        <Input
          onChange={(e) => {
            setCompetentPerson(e.target.value);
          }}
          inputWidth="xl"
          value={competentPerson}
        />
      </FormGroup>

      <div id={id + '_inspectionDate'}>
        <FormDate
          id={id + '_inspectionDate'}
          name={id + '_inspectionDate'}
          label={labels.inspectionDateLabel}
          className={styles.inspectionDate}
          onChange={(e) => {
            setInspectionDate(e.value);
          }}
          value={inspectionDate}
          required={true}
          hasError={showErrors && errors[`${id}_inspectionDate`]?.invalid}
          errorMessage={errors[`${id}_inspectionDate`]?.errorMessage}
          validateOnFly={false}
        />
      </div>

      {inspectionDateWarning && <InPageAlert title={inspectionDateWarning} variant="warning" />}

      <FormRadioGroup
        id={id + '_anySpecialConditions'}
        name={id + '_anySpecialConditions'}
        vertical={true}
        options={radioOptionsYesNo}
        onChange={(v) => {
          setAnySpecialConditions(v);
          if (v === 'no') setSpecialConditions('');
        }}
        value={anySpecialConditions}
        errorMessage={errors[`${id}_anySpecialConditions`]?.errorMessage}
        hasError={showErrors && errors[`${id}_anySpecialConditions`]?.invalid}
        legend={labels.anySpecialConditionsLabel}
        margin={{ top: 'lg' }}
      />

      {anySpecialConditions === 'yes' && (
        <FormGroup
          id={id + '_conditions'}
          label={labels.describeConditionsLabel}
          margin={{ top: 'lg' }}
          errorMessage={errors[`${id}_conditions`]?.errorMessage}
          hasError={showErrors && errors[`${id}_conditions`]?.invalid}
        >
          <Input
            onChange={(e) => {
              setSpecialConditions(e.target.value);
            }}
            value={specialConditions}
          />
        </FormGroup>
      )}

      <div className={`${styles.labelMsg} ${styles.marginTopOneHalf}`} id={id + '_upload'}>
        {labels.supportingDocumentsLabel}
      </div>
      {maxFiles > 1 && (
        <div className={styles.helpMsg}>
          Please upload a copy of the certificate and an officially translated copy, if the original document is not in
          English.
        </div>
      )}
      {maxFiles == 1 && (
        <div>
          <ol className={styles.listItem}>
            <li>Documents must be in English.</li>
            <li>
              If you are uploading a scanned copy of a print document, check the image is clear and all text is legible.
            </li>
            <li>Upload one file for each document. This includes scanning a multi-page document into a single file.</li>
          </ol>
        </div>
      )}
      <FileUploadPattern
        api={attachmentApi}
        name={id + '_upload'}
        id={id + '_upload'}
        legendText=""
        initialFiles={uploadedFiles}
        onUploaded={(value) => setUploadedFiles(value)}
        onRemoved={(value) => setUploadedFiles(value)}
        maxMBSize={10}
        acceptedFormats=".png,.PNG,.jpg,.JPG,.jpeg,.JPEG,.pdf,.PDF"
        maxFiles={maxFiles}
        errorMessage={errors[`${id}_upload`]?.errorMessage}
      />
    </>
  );
});

InspectionDocumentsUpload.displayName = 'InspectionDocumentsUpload';
